import { ImageName } from "@mcleod/images";
import { DataSourceMode, Dialog, Label, Layout, LayoutProps, Panel } from "@mcleod/components";
import { UnassignEquipmentPrompt } from "./UnassignEquipmentPrompt";
import { getDispatchControlBoolean } from "@mcleod/general";
import { HorizontalAlignment } from "@mcleod/core";

interface UnassignEquipmentDialogLayoutProps extends LayoutProps {
    codeType: string;
    driverName?: string;
    tractorId?: string;
    isMultiSelect?: boolean;
    isLinkedMove?: boolean;
}

export class UnassignEquipmentDialog extends Dialog {
    private _codeType: string;
    private _driverName?: string;
    private _tractorId?: string;
    private _isMultiSelect?: boolean = false;
    private _isLinkedMove?: boolean = false;
    private layout: UnassignEquipmentPrompt

    constructor(props?: Partial<UnassignEquipmentDialogLayoutProps>) {
        super({
            title: "Unassign Equipment",
            minWidth: "500",
            okVisible: false,
            borderShadow: true,
            fillHeight: false,
            fillRow: false,
            panelTitleProps: {
                backgroundColor: "primary.darker",
                maxHeight: 55,
                fillRow: true,
                paddingLeft: 0,
                paddingRight: 8
            },
            titleProps: {
                color: "default.reverse",
                fontBold: true,
                fontSize: "large",
                imageColor: "primary.light",
                imageName: ImageName.circleX,
                rowBreak: false,
                marginLeft: 16
            }
        });

        this._codeType = props.codeType;
        this._driverName = props.driverName;
        this._tractorId = props.tractorId;
        this._isMultiSelect = props.isMultiSelect;
        this._isLinkedMove = props.isLinkedMove;
        this.buttonClose.setProps({
            color: "primary.reverse"
        });
    }

    override async show(): Promise<any> {
        this.layout = Layout.getLayout("lme/dispatch/UnassignEquipmentPrompt") as UnassignEquipmentPrompt;
        const showReason: boolean = getDispatchControlBoolean("req_unassign_reason", false);
        this.layout.addLayoutLoadListener(() => {
            this.layout.panelMessage.add(this.createOverwritePanel());
            if (showReason) {
                this.layout.mainDataSource.mode = DataSourceMode.ADD;
                this.layout.panelUnassignReason.enabled = true; 
                this.layout.panelUnassignReason.visible = true;                
                this.layout.textboxReasonCode.addBeforeLookupModelSearchListener((event) => {
                    event.filter.code_type = this._codeType;
                });
            }
            else {
                this.layout.textboxComments.required = false;
                this.layout.textboxReasonCode.required = false;
            }
            this.layout.buttonNo.addClickListener(() => this.close(false));
            this.layout.buttonYes.addClickListener(() => {
                if (this.layout.mainDataSource.validate()) {
                    if (!showReason) {
                        this.close(true);
                        return;
                    }
                    this.close(this.layout.activeRow);
                }
            });
        });
        this.add(this.layout);
        return super.show();
    }

    private createOverwritePanel(): Panel {
        const message = new Label({
            text: this.getOverwriteMessage(),
            align: HorizontalAlignment.CENTER
        });
        const question = new Label({
            fontBold: true, fontSize: "large",
            text: "Would you like to unassign this equipment?"
        });
        return new Panel({
            width: 550,
            align: HorizontalAlignment.CENTER,
            components: [message, question]
        });
    }

    private getOverwriteMessage(): string {
        const driverName = this._driverName;
        const tractor = this._tractorId;
        const isMultiSelect = this._isMultiSelect;
        const isLinkedMove = this._isLinkedMove;
        if (isMultiSelect || isLinkedMove) {
            return "These movements are currently assigned.";
        }
        if (driverName && tractor) {
            return `This movement is currently assigned to ${driverName} in Tractor ${tractor}.`;
        } else if (!driverName && tractor) {
            return `This movement is currently assigned to Tractor ${tractor}.`;
        } else if (driverName && !tractor) {
            return `This movement is currently assigned to ${driverName}.`;
        }
    }
}
